import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { formatDate } from "@/lib/utils-format";
import ArticleCard from "@/components/public/ArticleCard";
import Newsletter from "@/components/public/Newsletter";
import AdSlot from "@/components/public/AdSlot";
import CategoryCarousel from "@/components/public/CategoryCarousel";
import SEO from "@/components/SEO";
import { useSite } from "@/context/SiteContext";

function Hero() {
  const [hero, setHero] = useState(null);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => { api.get("/hero").then((r) => setHero(r.data)).catch(() => {}); }, []);
  if (hero && hero.active === false) return null;

  const title = hero?.title || "Sunset Magazine";
  const subtitle = hero?.subtitle || "Historias que definen el estilo de vida.";
  const img = mediaUrl(hero?.fallback_image);
  const video = mediaUrl(hero?.video_url);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-screen w-full overflow-hidden bg-sunset-ink">
      <motion.div style={{ y }} className="absolute inset-0">
        {video ? (
          <video autoPlay muted loop playsInline poster={img} className="w-full h-full object-cover">
            <source src={video} type="video/mp4" />
          </video>
        ) : (
          img && <img src={img} alt="" className="w-full h-full object-cover" />
        )}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />

      <motion.div style={{ opacity }} className="relative h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-24 md:pb-28">
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="kicker text-white/80 mb-5">Sunset Magazine RD · Edición Digital</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35 }}
          className="font-serif text-white text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight max-w-4xl">
          {title}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.55 }}
          className="text-white/80 text-lg md:text-xl mt-6 max-w-xl leading-relaxed">{subtitle}</motion.p>
        {hero?.button_text && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7 }} className="mt-9">
            <Link to={hero.button_url || "/articulos"} data-testid="hero-cta-button"
              className="group inline-flex items-center gap-3 bg-white text-sunset-ink kicker px-9 py-4 hover:bg-sunset-accent hover:text-white transition-colors">
              {hero.button_text}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        )}
      </motion.div>

      <motion.div style={{ opacity }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70">
        <ArrowDown size={22} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

function Featured({ article }) {
  if (!article) return null;
  return (
    <section data-testid="featured-section" className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
      <div className="flex items-center gap-4 mb-10">
        <span className="kicker text-sunset-accent">Historia Destacada</span>
        <span className="flex-1 h-px bg-sunset-border" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
        <Link to={`/articulo/${article.slug}`} className="md:col-span-8 img-zoom aspect-[16/10] bg-sunset-border block">
          <img src={mediaUrl(article.cover_image)} alt={article.title} className="w-full h-full object-cover" />
        </Link>
        <div className="md:col-span-4">
          {article.category && (
            <Link to={`/categoria/${article.category.slug}`} className="kicker text-sunset-accent">{article.category.name}</Link>
          )}
          <Link to={`/articulo/${article.slug}`}>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight mt-4 mb-5 hover:text-sunset-accent transition-colors">
              {article.title}
            </h2>
          </Link>
          <p className="text-sunset-muted leading-relaxed mb-6">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-sunset-muted mb-8">
            {article.author && <span className="font-medium text-sunset-ink">{article.author.name}</span>}
            <span>·</span><span>{formatDate(article.published_at)}</span>
            {article.reading_time && <><span>·</span><span>{article.reading_time} min de lectura</span></>}
          </div>
          <Link to={`/articulo/${article.slug}`} data-testid="featured-read-button"
            className="group inline-flex items-center gap-3 border-b-2 border-sunset-ink pb-1 kicker hover:border-sunset-accent hover:text-sunset-accent transition-colors">
            Leer artículo <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState(null);
  const { categories } = useSite();

  useEffect(() => {
    api.get("/articles?featured=true&limit=1").then((r) => {
      if (r.data?.length) setFeatured(r.data[0]);
    }).catch(() => {});
    api.get("/articles?limit=13").then((r) => setArticles(r.data || [])).catch(() => {});
  }, []);

  const list = articles.filter((a) => !featured || a.id !== featured.id);
  const lead = list[0];
  const secondary = list.slice(1, 3);
  const grid = list.slice(3, 11);

  return (
    <div>
      <SEO />
      <Hero />
      <Featured article={featured} />
      <AdSlot position="Billboard" className="pb-8 px-5" />

      <section data-testid="home-categories" className="py-8 md:py-12 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <p className="kicker text-sunset-accent mb-3">Explora por sección</p>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight">Categorías</h2>
            </div>
            <Link to="/categorias" className="kicker text-sunset-muted hover:text-sunset-accent transition-colors hidden md:block whitespace-nowrap">Ver todas</Link>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto pl-5 md:pl-10">
          <CategoryCarousel categories={categories} />
        </div>
      </section>

      <section data-testid="latest-articles" className="max-w-[1400px] mx-auto px-5 md:px-10 pb-8">
        <div className="flex items-center gap-4 mb-12">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight">Últimos Artículos</h2>
          <span className="flex-1 h-px bg-sunset-border" />
          <Link to="/articulos" className="kicker text-sunset-muted hover:text-sunset-accent transition-colors hidden md:block">Ver todos</Link>
        </div>

        {lead && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-16">
            <ArticleCard article={lead} size="lg" index={0} />
            <div className="grid grid-cols-1 gap-8">
              {secondary.map((a, i) => <ArticleCard key={a.id} article={a} size="md" index={i + 1} />)}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {grid.map((a, i) => <ArticleCard key={a.id} article={a} size="sm" index={i} />)}
        </div>
      </section>

      <div className="pt-16"><Newsletter /></div>
    </div>
  );
}
